/**
 * useInvitationGate — controleaza accesul pe site.
 *
 * Flow NOU (cu camera):
 *  1. La mount, citeste visitors.authorized_at pentru device-ul curent
 *  2. Daca NULL, returneaza needCamera=true
 *  3. UI-ul afiseaza QrScanner (camera)
 *  4. QrScanner decodeaza QR-ul, extrage URL-ul cu ?q=<short_code>
 *  5. Apeleaza tryAuthorize(url) -> valideaza token-ul in DB
 *  6. Daca OK, marcheaza visitors.authorized_at = now()
 *
 * URL-urile din QR nu mai sunt "shortcut-uri" pentru intrare — ele trec prin
 * acelasi flow de validare ca orice scanare de camera.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { getDeviceId } from '@/lib/device';

const AUTHORIZED_KEY = 'guestlens_authorized';

interface GateState {
  loading: boolean;
  authorized: boolean;
  /** Set true cand tocmai s-a autorizat (UI afiseaza mesaj de bun venit). */
  justAuthorized: boolean;
}

function readLocalAuth(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(AUTHORIZED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeLocalAuth(yes: boolean) {
  if (typeof window === 'undefined') return;
  try {
    if (yes) localStorage.setItem(AUTHORIZED_KEY, '1');
    else localStorage.removeItem(AUTHORIZED_KEY);
  } catch {
    // localStorage plin / disabled
  }
}

async function fetchRemoteAuthorized(): Promise<boolean> {
  const deviceId = getDeviceId();
  if (!deviceId) return false;
  try {
    const { data, error } = await supabase
      .from('visitors')
      .select('authorized_at')
      .eq('device_id', deviceId)
      .maybeSingle();
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[useInvitationGate] fetch failed:', error.message);
      return false;
    }
    return !!data?.authorized_at;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[useInvitationGate] fetch exception:', e);
    return false;
  }
}

/**
 * Extrage short_code dintr-un URL sau string de tipul:
 *   https://larisa-si-dani.pages.dev/?q=BSH9-UP46
 *   https://larisa-si-dani.pages.dev/?qr=BSH9-UP46
 *   https://larisa-si-dani.pages.dev/#q=BSH9-UP46
 * Sau cod direct (XXXX-XXXX).
 */
function extractShortCode(input: string): string | null {
  const trimmed = input.trim();
  try {
    const url = new URL(trimmed);
    const q = url.searchParams.get('q') || url.searchParams.get('qr');
    if (q) return q.trim() || null;
    if (url.hash) {
      const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
      const hq = hashParams.get('q') || hashParams.get('t');
      if (hq) return hq.trim() || null;
      const rawHash = url.hash.replace(/^#/, '').trim();
      if (/^[A-Z2-9]{4}-?[A-Z2-9]{4}$/i.test(rawHash)) {
        return rawHash.toUpperCase();
      }
    }
  } catch {
    // Nu e URL valid
  }
  // Short code direct (XXXX-XXXX, 8 chars cu sau fara dash)
  if (/^[A-Z2-9]{4}-?[A-Z2-9]{4}$/i.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  return null;
}

interface AuthResult {
  ok: boolean;
  /** De ce a esuat (pt UI). */
  reason?: 'invalid' | 'expired' | 'consumed' | 'error';
}

export type { AuthResult };

/**
 * Valideaza un short_code si, daca e OK, autorizeaza device-ul curent.
 */
async function tryAuthorizeFromShortCode(shortCode: string): Promise<AuthResult> {
  const deviceId = getDeviceId();
  if (!deviceId) return { ok: false, reason: 'error' };

  // 1. Citim randul din qr_tokens (anon poate citi DOAR valid=true)
  const { data: tokenRow, error: fetchErr } = await supabase
    .from('qr_tokens')
    .select('short_code, valid')
    .eq('short_code', shortCode)
    .eq('valid', true)
    .maybeSingle();

  if (fetchErr) {
    // eslint-disable-next-line no-console
    console.warn('[useInvitationGate] token fetch failed:', fetchErr.message);
    return { ok: false, reason: 'error' };
  }
  if (!tokenRow) {
    return { ok: false, reason: 'invalid' };
  }

  // 2. Marcam device-ul ca autorizat (upsert: creeaza randul daca nu exista inca)
  const { error: updateErr } = await supabase
    .from('visitors')
    .upsert(
      {
        device_id: deviceId,
        authorized_at: new Date().toISOString(),
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : undefined,
      },
      { onConflict: 'device_id' }
    );

  if (updateErr) {
    // eslint-disable-next-line no-console
    console.warn('[useInvitationGate] authorize failed:', updateErr.message);
    return { ok: false, reason: 'error' };
  }

  return { ok: true };
}

export function useInvitationGate() {
  // Initial: localStorage instant (daca userul tocmai a scanat, avem deja cache)
  const [state, setState] = useState<GateState>({
    loading: true,
    authorized: readLocalAuth(),
    justAuthorized: false,
  });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // Verificam daca avem token in URL (la scanarea cu camera telefonului)
      const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
      const initialCode = extractShortCode(currentUrl);

      if (initialCode) {
        const authRes = await tryAuthorizeFromShortCode(initialCode);
        if (cancelled) return;
        if (authRes.ok) {
          writeLocalAuth(true);
          // Curatam URL-ul instantaneu: eliminam tokenul din bara de adrese
          try {
            window.history.replaceState(null, '', window.location.pathname);
          } catch {}
          setState({
            loading: false,
            authorized: true,
            justAuthorized: true,
          });
          setTimeout(() => {
            if (!cancelled) setState((s) => ({ ...s, justAuthorized: false }));
          }, 5000);
          return;
        }
      }

      // Altfel, verificam daca device-ul e deja autorizat in Supabase
      const remoteAuthorized = await fetchRemoteAuthorized();
      if (cancelled) return;
      writeLocalAuth(remoteAuthorized);
      setState({
        loading: false,
        authorized: remoteAuthorized,
        justAuthorized: false,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Apelata de QrScanner cand detecteaza un QR.
   * Returneaza AuthResult ca UI-ul sa stie ce sa afiseze.
   */
  const tryAuthorize = useCallback(async (input: string): Promise<AuthResult> => {
    const shortCode = extractShortCode(input);
    if (!shortCode) {
      return { ok: false, reason: 'invalid' };
    }

    const result = await tryAuthorizeFromShortCode(shortCode);
    if (result.ok) {
      writeLocalAuth(true);
      setState({ loading: false, authorized: true, justAuthorized: true });
      // dupa 5 secunde, ascundem mesajul "Bine ai venit"
      setTimeout(() => {
        setState((s) => ({ ...s, justAuthorized: false }));
      }, 5000);
    }
    return result;
  }, []);

  return {
    loading: state.loading,
    authorized: state.authorized,
    justAuthorized: state.justAuthorized,
    tryAuthorize,
  };
}
